import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { maxLength } from 'utils/validation-utils'
import { validationContext } from 'validx'

/**
 * View Model for the Reopen Job as Admin dialog.
 * @extends ModalViewModel
 */
export default class ReopenJobAsAdminViewModel extends ModalViewModel {
  /**
   * Disables submit button.
   */
  @observable disabled = true

  /**
   * The reason for reopening the job.
   */
  @observable reason = ''

  /**
   * Hold the maximum length of reason field.
   */
  private readonly reasonLength = {
    reason: { max: 100 },
  }

  /**
   * Helper text for reason input field.
   */
  reasonHelpText = `Why are you reopening?
    This text is public and can be seen by both Client and Pros.`

  /**
   * Validation context for View Model.
   */
  validation = validationContext(this, {
    reason: [
      maxLength(
        this.reasonLength.reason.max,
        `Reason must be less than ${this.reasonLength.reason.max} characters.`,
      ),
    ],
  })

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private newAlert: AlertFactory,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.reason = ''
    this.disabled = true

    this.reopenJobAsAdmin.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Reopens job as admin.
   */
  reopenJobAsAdmin = task.resolved(async () => {
    if (!this.validate()) {
      return
    }
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying('Reopening Job', `Reopening ${this.job.name}...`)
      .autoDismiss()
      .show()

    await this.jobStore.reopenJobAsAdmin(Id.assert(this.job.id), this.reason)

    alert
      .withState(AlertState.Success)
      .saying('Success!', `${this.job.name} is reopened.`)
      .autoDismiss()

    this.close()
  })

  /**
   * Shows Reopen Job as Admin Dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }
}
